

// ==========================================
// BUYERHEADER.JSX - UPDATED VERSION
// Estate Experts - Buyer Dashboard Header
// With Notification Dropdown, Profile Dropdown & Active Navigation
// ==========================================

import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaSearch, 
  FaCog, 
  FaBell, 
  FaHeart,
  FaChevronDown,
  FaUser,
  FaHome,
  FaCalendarCheck,
  FaComments,
  FaCreditCard,
  FaSignOutAlt,
  FaCheckCircle,
  FaInfoCircle,
  FaExclamationTriangle,
  FaBellSlash
} from 'react-icons/fa';
import '../../../assets/css/admin/adminheader/adminheader.css';
import AppContext from 'context/AppContext';
import axios from 'axios';
import { FiAlertTriangle, FiBell, FiCalendar, FiMessageSquare, FiStar } from 'react-icons/fi';

function AdminHeader({ pageTitle = 'Dashboard', isSidebarExpanded = false }) {
  const navigate = useNavigate();
  
  // ==========================================
  // REFS FOR CLICK OUTSIDE DETECTION
  // ==========================================
  const {loggedInAdmin,baseUrl,authToken}=useContext(AppContext);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const searchRef = useRef(null);

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  
  // Dropdowns state
  const [showNotifications, setShowNotifications] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const viewAllNotification = () => {
    axios.get(`${baseUrl}/admin/notification/viewAllNotification` ,{

      headers: {
        "Authorization": `Bearer ${authToken}`
      },
    })
      .then((response) => {
        console.log(response.data);
        if (response.data.success == true) {
          const updatedNotifications = response.data.notifications.map((item) => {
            let icon = <FiBell />;
            let color = "#3b82f6";

            if (item.category === "booking") {
              icon = <FiCalendar />;
              color = "#3b82f6";
            }

            else if (item.category === "property") {
              icon = <FaHome />;
              color = "#10b981";
            }

            else if (item.category === "warning") {
              icon = <FiAlertTriangle />;
              color = "#dc2626";
            }

            else if (item.category === "message") {
              icon = <FiMessageSquare />;
              color = "#8b5cf6";
            }

            else if (item.category === "rating") {
              icon = <FiStar />;
              color = "#f59e0b";
            }
            return {
              ...item,
              icon,
              color
            };

          });
          setNotifications(updatedNotifications);
        }

      }).catch((error) => {
        console.log(error);
      })
  }
  useEffect(()=>{
    viewAllNotification();
  },[]);

  // ==========================================
  // CLICK OUTSIDE TO CLOSE DROPDOWNS
  // ==========================================
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close notifications
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      
      // Close profile dropdown
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }

      // Close search results
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ==========================================
  // HANDLER FUNCTIONS
  // ==========================================
  
  // Toggle notification dropdown
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setIsProfileDropdownOpen(false);
  };

  // Toggle profile dropdown
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    setShowNotifications(false);
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, unread: false })));
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications(notifications.map(n => 
      n.id === notification.id ? { ...n, unread: false } : n
    ));
    
    // Navigate based on notification type
    // Add your navigation logic here
    console.log('Notification clicked:', notification);
  };

  // Show more notifications (navigate to notifications page)
  const handleShowMoreNotifications = () => {
    setShowNotifications(false);
    navigate('/admin/notification');
  };

  // Handle search
  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === '') {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    // Simulate API call (Replace with actual API)
    setTimeout(() => {
      // Mock search results
      const mockResults = [
        {
          id: 1,
          title: '5 Marla House in DHA Phase 5',
          location: 'DHA, Lahore',
          price: 'PKR 2.5 Crore',
          image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=200&h=200&fit=crop'
        },
        {
          id: 2,
          title: '10 Marla House in Bahria Town',
          location: 'Bahria Town, Lahore',
          price: 'PKR 1.8 Crore',
          image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=200&h=200&fit=crop'
        },
        {
          id: 3,
          title: '1 Kanal House in Model Town',
          location: 'Model Town, Lahore',
          price: 'PKR 3.2 Crore',
          image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&h=200&fit=crop'
        }
      ].filter(property => 
        property.title.toLowerCase().includes(query.toLowerCase()) ||
        property.location.toLowerCase().includes(query.toLowerCase())
      );

      setSearchResults(mockResults);
      setIsSearching(false);
    }, 500);
  };

  // Handle search result click
  const handleSearchResultClick = (property) => {
    setShowSearchResults(false);
    setSearchQuery('');
    navigate(`/admin/property/${property.id}`);
  };

  // Handle dropdown menu item click
  const handleMenuItemClick = (item) => {
    setIsProfileDropdownOpen(false);
    navigate(item.path);
  };

  // Handle logout
  const handleLogout = () => {
    // Clear authentication
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userProfile');
    
    // Show logout message
    console.log('Logout successful');
    
    // Redirect to login
    navigate('/auth');
  };

  // ==========================================
  // PROFILE DROPDOWN MENU ITEMS
  // ==========================================
  
  const profileMenuItems = [
    { id: 1, label: 'My Profile', icon: <FaUser />, path: '/admin/profile' },
    // { id: 2, label: 'Settings', icon: <FaCog />, path: '/admin/setting' }
  ];

  // ==========================================
  // CALCULATE UNREAD NOTIFICATIONS COUNT
  // ==========================================
  
  const unreadCount = notifications.filter(n => n.unread).length;

  // ==========================================
  // RENDER
  // ==========================================
  
  return (
   <header className={`adminheader-container ${isSidebarExpanded ? 'adminheader-expanded' : 'adminheader-collapsed'}`}>
      <div className="adminheader-wrapper">
        
        {/* ==========================================
            LEFT SECTION - Page Title
            ========================================== */}
        <div className="adminheader-left">
          <h1 className="adminheader-page-title">{pageTitle}</h1>
        </div>

        {/* ==========================================
            CENTER SECTION - Search Bar
            ========================================== */}
        <div className="adminheader-center" ref={searchRef}>
          

          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div className="adminheader-search-results">
              {isSearching ? (
                <div className="adminheader-search-no-results">
                  Searching...
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((property) => (
                  <div
                    key={property.id}
                    className="adminheader-search-result-item"
                    onClick={() => handleSearchResultClick(property)}
                  >
                    <img
                      src={property.image}
                      alt={property.title}
                      className="adminheader-search-result-image"
                    />
                    <div className="adminheader-search-result-details">
                      <div className="adminheader-search-result-title">
                        {property.title}
                      </div>
                      <div className="adminheader-search-result-info">
                        {property.location} • {property.price}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="adminheader-search-no-results">
                  No properties found matching "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>

        {/* ==========================================
            RIGHT SECTION - Actions + Profile
            ========================================== */}
        <div className="adminheader-right">
          
          {/* Action Buttons */}
          <div className="adminheader-actions">
            
            {/* Notifications Button with Dropdown */}
            <div className="adminheader-notification-dropdown" ref={notificationRef}>
              <button 
                className="adminheader-action-btn adminheader-notification-btn"
                onClick={toggleNotifications}
                aria-label="Notifications"
              >
                <FaBell />
                {unreadCount > 0 && (
                  <span className="adminheader-notification-count">{unreadCount}</span>
                )}
              </button>

              {/* Notification Panel */}
              {showNotifications && (
                <div className="adminheader-notification-panel">
                  {/* Header */}
                  <div className="adminheader-notification-header">
                    <h3 className="adminheader-notification-title">Notifications</h3>
                    {unreadCount > 0 && (
                      <button 
                        className="adminheader-mark-all-read"
                        onClick={markAllAsRead}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  {/* Notification List */}
                  {notifications.length > 0 ? (
                    <>
                      <div className="adminheader-notification-list">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`adminheader-notification-item ${notification.unread ? 'unread' : ''}`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className={`adminheader-notification-icon-wrapper ${notification.category}`}>
                              {notification.icon}
                            </div>
                            <div className="adminheader-notification-content">
                              <div className="adminheader-notification-message">
                                {notification.title}
                              </div>
                              <div className="adminheader-notification-time">
                                {new Date(notification.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Footer with Show More */}
                      <div className="adminheader-notification-footer">
                        <button 
                          className="adminheader-show-more-btn"
                          onClick={handleShowMoreNotifications}
                        >
                          Show More
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="adminheader-no-notifications">
                      <div className="adminheader-no-notifications-icon">
                        <FaBellSlash />
                      </div>
                      <div className="adminheader-no-notifications-text">
                        No notifications yet
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Profile Dropdown */}
          <div className="adminheader-profile-dropdown" ref={profileRef}>
            <button 
              className="adminheader-profile-btn"
              onClick={toggleProfileDropdown}
            >
              {/* <img 
                src={adminProfile.avatar} 
                alt={adminProfile.name}
                className="adminheader-profile-avatar"
              /> */}
              <span className="adminheader-profile-name">{loggedInAdmin?.fullName}</span>
              <FaChevronDown className={`adminheader-profile-arrow ${isProfileDropdownOpen ? 'adminheader-arrow-up' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="adminheader-dropdown-menu">
                {/* Profile Info Section */}
                <div className="adminheader-dropdown-profile-info">
                  {/* <img 
                    src={adminProfile.avatar} 
                    alt={adminProfile.name}
                    className="adminheader-dropdown-avatar"
                  /> */}
                  <div className="adminheader-dropdown-profile-details">
                    <div className="adminheader-dropdown-name">{loggedInAdmin?.fullName}</div>
                    <div className="adminheader-dropdown-email">{loggedInAdmin?.email}</div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="adminheader-dropdown-items">
                  {profileMenuItems.map((item) => (
                    <button
                      key={item.id}
                      className="adminheader-dropdown-item"
                      onClick={() => handleMenuItemClick(item)}
                    >
                      <span className="adminheader-dropdown-icon">{item.icon}</span>
                      <span className="adminheader-dropdown-label">{item.label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Logout Button */}
                <button
                  className="adminheader-dropdown-item adminheader-logout-item"
                  onClick={handleLogout}
                >
                  <span className="adminheader-dropdown-icon"><FaSignOutAlt /></span>
                  <span className="adminheader-dropdown-label">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default AdminHeader;